import Breadcrumb from "components/Common/Breadcrumb"
import React, { useState, useMemo, useEffect } from "react"
import {
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaFileImport,
  FaPlus,
  FaSave,
} from "react-icons/fa"
import { MdCancel, MdDelete } from "react-icons/md"
import {
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Input,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
} from "reactstrap"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import { usePermissionData } from "AppContext"

const DepartmentManagement = () => {
  document.title = "Departments | Milano Jewelry"
  const { checkAccess } = usePermissionData()

  // State
  const [departments, setDepartments] = useState([])

  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingDept, setEditingDept] = useState(null)
  const [searchText, setSearchText] = useState("")
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)

  /* ================= SAMPLE DATA ================= */
  useEffect(() => {
    getDepartment()
  }, [])

  async function getDepartment() {
    const APIdata = await fetchData(setLoading, "department")
    if (APIdata?.success) {
      setDepartments(APIdata?.data)
    }
  }

  // Toggle modal
  const toggleModal = () => setModalOpen(!modalOpen)

  // Open modal for Edit
  const handleEdit = dept => {
    setEditingDept(dept)
    toggleModal()
  }

  const handleSave = async () => {
    if (!editingDept?.departmentName?.trim()) {
      Swal.fire({ title: "Department name is required", icon: "warning" })
      return
    }
    if (editingDept?.id) {
      const APIdata = await FetchPutData(
        setLoading,
        "department",
        editingDept?.id,
        editingDept
      )
      if (APIdata?.data?.success) {
        setModalOpen(false)
        Swal.fire({
          title: "Department Updated Successfully",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setTimeout(() => {
          getDepartment()
        }, 1500)
      } else {
        Swal.fire({
          title: "Error Adding Department",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    } else {
      const APIdata = await FetchPostData(setLoading, "department", editingDept)
      if (APIdata?.data?.success) {
        setModalOpen(false)
        Swal.fire({
          title: "Department Added Successfully",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setTimeout(() => {
          getDepartment()
        }, 1500)
      } else {
        Swal.fire({
          title: "Error Adding Department",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    }
  }

  const handleDelete = async item => {
    const confirm = await Swal.fire({
      title: "Are you Sure?",
      text: `you want to Delete this ${item?.departmentName} department`,
      icon: "warning",
      showCancelButton: true,
    })

    if (!confirm.isConfirmed) {
      return
    }
    const APIdata = await FetchDeleteData(setLoading, "department", item?.id)
    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Department Deleted Successfully",
        text: APIdata?.data?.message,
        icon: "success",
      })
      setTimeout(() => {
        getDepartment()
      }, 1500)
    } else {
      Swal.fire({
        title: "Error deleting Role",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  // Filtered & paginated departments
  const filteredDepts = useMemo(
    () =>
      departments.filter(d =>
        d?.departmentName?.toLowerCase().includes(searchText.toLowerCase())
      ),
    [departments, searchText]
  )

  const totalPages = Math.ceil(filteredDepts.length / perPage)
  const paginatedDepts = filteredDepts.slice(
    (page - 1) * perPage,
    page * perPage
  )

  return (
    <div className="page-content">
      <Breadcrumb title="Settings" breadcrumbItem="Departments" />
      <Card>
        <CardBody>
          {/* ===== FILTER BAR ===== */}
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex gap-3">
              <select
                className="form-select"
                style={{ width: "100px" }}
                value={perPage}
                onChange={e => {
                  setPerPage(Number(e.target.value))
                  setPage(1)
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>

              <Input
                placeholder="Search..."
                value={searchText}
                onChange={e => {
                  setSearchText(e.target.value)
                  setPage(1)
                }}
              />
            </div>

            {checkAccess(7, "add") && (
              <Button
                className="btn-blue"
                onClick={() => {
                  toggleModal(), setEditingDept({})
                }}
              >
                <FaPlus className="me-1" />
                Add
              </Button>
            )}
          </div>

          <Table bordered responsive hover>
            <thead>
              <tr>
                <th>ID</th>
                <th>Department Name</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDepts.length > 0 ? (
                paginatedDepts.map((dept, index) => (
                  <tr key={dept.id}>
                    <td>{(page - 1) * perPage + index + 1}</td>
                    <td>{dept.departmentName}</td>
                    <td>
                      {checkAccess(7, "edit") && (
                        <Button
                          color="warning"
                          className="me-2"
                          onClick={() => handleEdit(dept)}
                        >
                          <FaEdit />
                        </Button>
                      )}
                      {checkAccess(7, "delete") && (
                        <Button
                          color="danger"
                          onClick={() => handleDelete(dept)}
                        >
                          <MdDelete />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center">
                    No departments found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* ===== PAGINATION ===== */}
          <div className="d-flex justify-content-between">
            <span className="align-self-center">
              Page {page} of {totalPages || 1}
            </span>
            {/* ===== PAGINATION ===== */}
            <div className="d-flex justify-content-center align-items-center gap-2 ">
              {/* Previous */}
              <Button
                disabled={page === 1}
                onClick={() => setPage(prev => prev - 1)}
                className="btn-blue"
              >
                <FaChevronLeft />
              </Button>

              {/* Page Numbers (Show 5 at a time) */}
              {(() => {
                const pageNumbers = []
                const maxVisiblePages = 5

                let startPage = Math.max(
                  1,
                  page - Math.floor(maxVisiblePages / 2)
                )

                let endPage = startPage + maxVisiblePages - 1

                if (endPage > totalPages) {
                  endPage = totalPages
                  startPage = Math.max(1, endPage - maxVisiblePages + 1)
                }

                for (let i = startPage; i <= endPage; i++) {
                  pageNumbers.push(
                    <Button
                      key={i}
                      color={page === i ? "primary" : "light"}
                      onClick={() => setPage(i)}
                    >
                      {i}
                    </Button>
                  )
                }

                return pageNumbers
              })()}

              {/* Next */}
              <Button
                disabled={page === totalPages || totalPages === 0}
                onClick={() => setPage(prev => prev + 1)}
                className="btn-blue"
              >
                <FaChevronRight />
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={modalOpen} centered>
        <ModalHeader toggle={toggleModal}>
          {editingDept?.id ? "Edit Department" : "Add Department"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <Label>Department Name</Label>
            <Input
              type="text"
              value={editingDept?.departmentName || ""}
              onChange={e =>
                setEditingDept(prev => ({
                  ...prev,
                  departmentName: e.target.value.replace(/^\s+/, ""),
                }))
              }
            />
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button className="btn-blue" onClick={handleSave}>
            <FaSave /> Save
          </Button>{" "}
          <Button color="secondary" onClick={toggleModal}>
            <MdCancel /> Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default DepartmentManagement
